/**
 * FeatureMergePanel - Entity-specific merge panel for Features
 * 
 * Provides a familiar tab-based UI (Overview | Requirements) for both
 * source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'feature',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of feature objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged feature form data
 * - onFormChange: (field, value) => void
 * - productId: string
 * - errors: { [field]: string } - validation errors
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Check,
  Plus,
  Minus,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import FeatureOverviewForm, { DEFAULT_FEATURE_STATUSES, PRIORITY_LEVELS } from '../feature/FeatureOverviewForm';
import FeatureRequirementsPanel, { generateRequirementId } from '../feature/FeatureRequirementsPanel';

// =============================================================================
// SOURCE FEATURE CARD (Left Panel)
// =============================================================================

const SourceFeatureCard = ({
  feature,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  epics,
  releases,
  personas,
  availableTags,
  teamMembers,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const epicName = epics.find(e => e.epic_id === feature.epic_id)?.title || 'No Epic';
  const assigneeName = teamMembers.find(m => (m.user_id || m.member_id) === feature.assignee_id)?.name || 'Unassigned';
  const requirementCount = feature.requirements?.length || 0;
  const completedReqs = (feature.requirements || []).filter(r => r.is_completed).length;
  
  // Get status styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'Done': return 'bg-green-500';
      case 'In Progress': return 'bg-blue-500';
      case 'Review': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-emerald-300 dark:border-emerald-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-feature-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Feature Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isSelected ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-gray-200 dark:bg-gray-700"
        )}>
          <Layers className={cn("w-4 h-4", isSelected ? "text-emerald-600" : "text-gray-500")} />
        </div>
        
        {/* Feature Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {feature.title || 'Untitled Feature'}
            </span>
            {feature.is_blocked && (
              <Badge variant="destructive" className="text-[10px] h-5">Blocked</Badge>
            )}
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className={cn("px-1.5 py-0.5 rounded", getStatusColor(feature.status), "text-white text-[10px]")}>
                {feature.status || 'Backlog'}
              </span>
              <span>•</span>
              <span>{epicName}</span>
              <span>•</span>
              <span>{assigneeName}</span>
              {requirementCount > 0 && (
                <>
                  <span>•</span>
                  <span>{completedReqs}/{requirementCount} criteria</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content - Tab-based View */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="overview" className="text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="requirements" className="text-xs">
                  Requirements ({requirementCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3">
            {activeTab === 'overview' && (
              <FeatureOverviewForm
                formData={feature}
                isEditing={false}
                isDisabled={true}
                colors={colors}
                epics={epics}
                releases={releases}
                personas={personas}
                availableTags={availableTags}
                teamMembers={teamMembers}
                mode="view"
              />
            )}
            
            {activeTab === 'requirements' && (
              <FeatureRequirementsPanel
                requirements={feature.requirements || []}
                isEditing={false}
                colors={colors}
                mode="view"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, mergedRequirements, colors }) => {
  // Calculate what's being kept vs lost
  const selectedFeatures = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedFeatures = sourceItems.filter((_, idx) => !selectedItems[idx]);
  
  // Count requirements
  const totalSelectedReqs = selectedFeatures.reduce((sum, f) => sum + (f.requirements?.length || 0), 0);
  const excludedReqs = excludedFeatures.reduce((sum, f) => sum + (f.requirements?.length || 0), 0);
  const mergedReqCount = mergedRequirements?.length || 0;

  if (excludedFeatures.length === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedFeatures.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedFeatures.length} feature(s) excluded: {excludedFeatures.map(f => `"${f.title}"`).join(', ')}
            </span>
          </div>
        )}
        {excludedReqs > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedReqs} acceptance criteria from excluded features will be lost
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged feature will have {mergedReqCount} criteria from {selectedFeatures.length} selected feature(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED FEATURE FORM (Right Panel)
// =============================================================================

const MergedFeatureForm = ({
  formData,
  onFormChange,
  epics,
  releases,
  personas,
  teamMembers,
  availableTags,
  colors,
  errors = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };
  
  // Requirement handlers
  const handleAddRequirement = (text) => {
    const newReq = {
      id: generateRequirementId(),
      text,
      is_completed: false,
    };
    onFormChange('requirements', [...(formData.requirements || []), newReq]);
  };
  
  const handleUpdateRequirement = (reqId, field, value) => {
    const updated = (formData.requirements || []).map(req => 
      req.id === reqId ? { ...req, [field]: value } : req
    );
    onFormChange('requirements', updated);
  };
  
  const handleDeleteRequirement = (reqId) => {
    const updated = (formData.requirements || []).filter(req => req.id !== reqId);
    onFormChange('requirements', updated);
  };
  
  const handleToggleRequirementComplete = (reqId, isCompleted) => {
    const updated = (formData.requirements || []).map(req => 
      req.id === reqId ? { ...req, is_completed: isCompleted } : req
    );
    onFormChange('requirements', updated);
  };
  
  // Tag handlers
  const handleTagAdd = (tagId) => {
    if (!formData.tag_ids?.includes(tagId)) {
      onFormChange('tag_ids', [...(formData.tag_ids || []), tagId]);
    }
  };
  
  const handleTagRemove = (tagId) => {
    onFormChange('tag_ids', (formData.tag_ids || []).filter(id => id !== tagId));
  };

  const requirementCount = formData.requirements?.length || 0;

  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => ['title', 'description', 'user_story'].includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("bg-gray-100 dark:bg-gray-800")}>
            <TabsTrigger 
              value="overview" 
              className={cn("text-sm", hasOverviewErrors && "text-red-500")}
            >
              Overview {hasOverviewErrors && '⚠️'}
            </TabsTrigger>
            <TabsTrigger value="requirements" className="text-sm">
              Requirements ({requirementCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <FeatureOverviewForm
            formData={formData}
            onChange={handleChange}
            isEditing={true}
            isDisabled={false}
            colors={colors}
            epics={epics}
            releases={releases}
            personas={personas}
            teamMembers={teamMembers}
            availableTags={availableTags}
            onTagAdd={handleTagAdd}
            onTagRemove={handleTagRemove}
            errors={errors}
            mode="create"
          />
        )}
        
        {activeTab === 'requirements' && (
          <FeatureRequirementsPanel
            requirements={formData.requirements || []}
            onAdd={handleAddRequirement}
            onUpdate={handleUpdateRequirement}
            onDelete={handleDeleteRequirement}
            onToggleComplete={handleToggleRequirementComplete}
            isEditing={true}
            colors={colors}
            mode="create"
          />
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const FeatureMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
  linkableEntities = {},  // Data from parent (epics, releases, personas, tags, teamMembers)
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Extract linkable entities from props (data flows down from parent)
  // This avoids redundant API calls - parent already has this data loaded
  const epics = linkableEntities.epic || linkableEntities.epics || [];
  const releases = linkableEntities.release || linkableEntities.releases || [];
  const personas = linkableEntities.persona || linkableEntities.personas || [];
  const availableTags = linkableEntities.tag || linkableEntities.tags || [];
  const teamMembers = linkableEntities.user || linkableEntities.teamMembers || [];
  
  // Initialize merged requirements from selected source features
  useEffect(() => {
    // Combine all requirements from selected features
    const selectedFeatures = sourceItems.filter((_, idx) => selectedItems[idx]);
    const allReqs = [];
    const seenText = new Set();
    
    selectedFeatures.forEach(feature => {
      (feature.requirements || []).forEach(req => {
        // Dedupe by text to avoid duplicates
        const normalizedText = (req.text || '').trim().toLowerCase();
        if (normalizedText && !seenText.has(normalizedText)) {
          seenText.add(normalizedText);
          allReqs.push({
            id: req.id || generateRequirementId(),
            text: req.text,
            is_completed: req.is_completed || false,
          });
        }
      });
    });
    
    // Only update if requirements haven't been set yet
    if (!formData.requirements || formData.requirements.length === 0) {
      onFormChange('requirements', allReqs);
    }
  }, [sourceItems, selectedItems]);
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Features */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Features
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select features to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((feature, idx) => (
            <SourceFeatureCard
              key={feature.feature_id || idx}
              feature={feature}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              epics={epics}
              releases={releases}
              personas={personas}
              availableTags={availableTags}
              teamMembers={teamMembers}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Feature Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined feature before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            mergedRequirements={formData.requirements}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedFeatureForm
            formData={formData}
            onFormChange={onFormChange}
            epics={epics}
            releases={releases}
            personas={personas}
            teamMembers={teamMembers}
            availableTags={availableTags}
            colors={colors}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureMergePanel;
